/**
 * The default confidence threshold for newly created templates.
 *
 * It is **device-local on purpose**, which is the honest form of a setting
 * the gateway does not have: `confidenceThreshold` is a column on the
 * template and every template is created at 0.8, with no user-level default
 * anywhere in the schema. The Flutter app keeps the same preference in
 * `GetStorage` and applies it after creating a template
 * (`app/lib/components/upload/template_dialog.dart`). Storing it in the
 * browser and saying so beats a control that looks account-wide and is not.
 *
 * Reading it is the other half, and the half that is easy to lose: the value
 * only means anything because a template-creating path PATCHes it onto the
 * new template (today `pages/onboarding/AnalysePage.tsx`). A stored
 * preference nobody reads is a screen lying to the person, so the test file
 * beside this one asserts that some path outside this folder still reads it.
 *
 * Every access is wrapped: `localStorage` throws in a private window and in
 * an embedded view, and a settings tab must not be the thing that breaks.
 */
export const DEFAULT_THRESHOLD_KEY = 'formalogix.defaultThreshold'

/** What the gateway creates a template at, so it is also what "unset" means. */
export const DEFAULT_THRESHOLD = 0.8
export const MIN_THRESHOLD = 0.5
export const MAX_THRESHOLD = 1

export function clampThreshold(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_THRESHOLD
  return Math.min(MAX_THRESHOLD, Math.max(MIN_THRESHOLD, value))
}

export function readDefaultThreshold(): number {
  let raw: string | null = null
  try {
    raw = window.localStorage.getItem(DEFAULT_THRESHOLD_KEY)
  } catch {
    return DEFAULT_THRESHOLD
  }
  if (raw === null) return DEFAULT_THRESHOLD
  const parsed = Number.parseFloat(raw)
  // Junk in the slot (a hand-edited value, an older format) reads as unset
  // rather than as a threshold of NaN that would flag every field.
  return Number.isFinite(parsed) ? clampThreshold(parsed) : DEFAULT_THRESHOLD
}

export function writeDefaultThreshold(value: number): void {
  try {
    window.localStorage.setItem(DEFAULT_THRESHOLD_KEY, String(clampThreshold(value)))
  } catch {
    /* A browser that refuses storage keeps the session's value and no more. */
  }
}

/** 0.83 -> "83 %", with the non-breaking space German typography wants. */
export function formatThreshold(value: number): string {
  return `${Math.round(value * 100)}\u00a0%`
}
